import { supabase } from './supabase-client'; // Import directly from supabase-client
import { PostgrestError } from '@supabase/supabase-js'; // Restore PostgrestError import
// import { SupabaseClient } from '@supabase/supabase-js'; // Removed unused import

// Import needed types
import { VitalSignRecord } from '../types/health-metric-types';
import { TestResult } from '../types/test-result-types';
// Import other types as needed
// import { PatientMedicationRecord, Medication } from '../types/medication-types';
// import { AppointmentRecord, Appointment } from '../types/appointment-types';

// --- Supabase Helper for Error Handling (Copied for isolation) ---
const handleSupabaseError = (error: PostgrestError | null, context: string): boolean => {
  if (error) {
    console.error(`Supabase error (${context}):`, error.message, `(Code: ${error.code})`);
    return true; // Indicates an error occurred
  }
  return false; // No error
};

// --- Specific Supabase Data Fetching Functions ---

/**
 * Fetches vital sign records for a specific patient from Supabase.
 * @param patientId The UUID of the patient.
 * @returns Promise resolving to an array of vital sign records.
 */
export const getVitalSigns = async (patientId: string): Promise<VitalSignRecord[]> => {
  if (!patientId) {
    console.warn('getVitalSigns: Patient ID is required.');
    return [];
  }

  const { data, error } = await supabase
    .from('vital_signs')
    // .schema('medical') // Chaining .schema() might be the issue if types are mismatched, try without it if 'medical' is default or RLS handles it
    .select('*')
    .eq('patient_id', patientId)
    .order('recorded_at', { ascending: false });

  if (handleSupabaseError(error, 'fetch vital signs')) {
    return []; // Return empty on error
  }
  
  return (data as VitalSignRecord[] | null) ?? [];
};

/**
 * Fetches test results for a specific patient from Supabase.
 * @param patientId The UUID of the patient.
 * @param testType Optional parameter to filter by test type (e.g., 'HbA1c')
 * @returns Promise resolving to an array of test results.
 */
export const getTestResults = async (patientId: string, testType?: string): Promise<TestResult[]> => {
  if (!patientId) {
    console.warn('getTestResults: Patient ID is required.');
    return [];
  }

  let query = supabase
    .from('test_results')
    .select('*')
    .eq('patient_id', patientId)
    .order('test_date', { ascending: false });

  // Apply test type filter if provided
  if (testType) {
    query = query.eq('test_name', testType);
  }

  const { data, error } = await query;

  if (handleSupabaseError(error, 'fetch test results')) {
    return []; // Return empty on error
  }
  
  return (data as TestResult[] | null) ?? [];
};

/**
 * Specialized function to fetch health metrics time-series data from test results
 * Extracts blood pressure, weight measurements, and HbA1c values
 * @param patientId The UUID of the patient
 * @returns Promise resolving to structured health metrics data
 */
export const getHealthMetricsFromTests = async (patientId: string) => {
  if (!patientId) {
    console.warn('getHealthMetricsFromTests: Patient ID is required.');
    return { bloodPressure: [], weight: [], hba1c: [] };
  }

  // Get blood pressure data
  const { data: bpData, error: bpError } = await supabase
    .from('test_results')
    .select('*')
    .eq('patient_id', patientId)
    .eq('test_name', 'Blood Pressure')
    .order('ordered_date', { ascending: true });

  if (handleSupabaseError(bpError, 'fetch blood pressure data')) {
    console.error('Error fetching blood pressure data:', bpError);
  }

  // Get weight measurements
  const { data: weightData, error: weightError } = await supabase
    .from('test_results')
    .select('*')
    .eq('patient_id', patientId)
    .eq('test_name', 'Weight')
    .order('ordered_date', { ascending: true });

  if (handleSupabaseError(weightError, 'fetch weight data')) {
    console.error('Error fetching weight data:', weightError);
  }

  // Get hemoglobin A1c data
  const { data: hba1cData, error: hba1cError } = await supabase
    .from('test_results')
    .select('*')
    .eq('patient_id', patientId)
    .eq('test_name', 'Hemoglobin A1c')
    .order('ordered_date', { ascending: true });

  if (handleSupabaseError(hba1cError, 'fetch HbA1c data')) {
    console.error('Error fetching HbA1c data:', hba1cError);
  } else {
    // console.log(`HbA1c data fetched successfully: ${hba1cData?.length || 0} records found`);
    // console.log('First HbA1c record:', hba1cData && hba1cData.length > 0 ? hba1cData[0] : 'No records');
  }

  const result = {
    bloodPressure: bpData || [],
    weight: weightData || [],
    hba1c: hba1cData || []
  };
  
  // console.log('getHealthMetricsFromTests returning data with counts:', {
  //   bloodPressureCount: result.bloodPressure.length,
  //   weightCount: result.weight.length,
  //   hba1cCount: result.hba1c.length
  // });
  
  return result;
};

// --- Add other patient-specific Supabase fetch functions below ---

// export const getPatientMedications = async (patientId: string): Promise<Medication[]> => { ... }
// export const getAppointments = async (patientId: string): Promise<Appointment[]> => { ... } 